import React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { transcriptsCopy } from '../../copy/transcripts';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// ConfidenceBadge — renders an AI-extraction confidence score (0..1) as a
// colour-coded chip. Green ≥ 0.8, amber 0.6–0.79, red < 0.6. Tooltip explains
// what the band means so users know whether to Accept or Dismiss at a glance.
// ---------------------------------------------------------------------------

interface ConfidenceBadgeProps {
  score: number;
}

export function ConfidenceBadge({ score }: ConfidenceBadgeProps) {
  const pct = Math.round(score * 100);
  const high = score >= 0.8;
  const mid = !high && score >= 0.6;
  const tone = high
    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
    : mid
      ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
      : 'bg-rose-500/15 text-rose-700 dark:text-rose-400';
  const tooltip = high
    ? transcriptsCopy.confidenceTooltipHigh
    : mid
      ? transcriptsCopy.confidenceTooltipMedium
      : transcriptsCopy.confidenceTooltipLow;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex min-w-[48px] items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
            tone,
          )}
        >
          {pct}%
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

export default ConfidenceBadge;
